import { useMemo } from "react"
import { useActiveBusinessId } from "@/hooks/use-business"
import { useGetBusinessQuery } from "@/queries/business/business"

export type BusinessPlanFeature =
  | "analytics"
  | "export"
  | "reports"
  | "bulk_import"

const FEATURE_MAP: Record<BusinessPlanFeature, string> = {
  analytics: "ANALYTICS",
  export: "EXPORT",
  reports: "REPORTS",
  bulk_import: "BULK_IMPORT",
}

type BusinessPlanRef = {
  tier?: string
  features?: string[]
}

type BusinessWithPlan = {
  approval?: { status?: string }
  businessPlan?: BusinessPlanRef | string
  subscriptionStatus?: string
}

function resolvePlanRef(business: BusinessWithPlan | undefined): BusinessPlanRef | undefined {
  const plan = business?.businessPlan
  if (!plan || typeof plan === "string") return undefined
  return plan
}

export function useBusinessPlanAccess() {
  const businessId = useActiveBusinessId()
  const { data: businessData, isLoading } = useGetBusinessQuery(businessId ?? "", {
    skip: !businessId,
  })

  return useMemo(() => {
    const business = businessData?.data as BusinessWithPlan | undefined
    const approvalStatus = business?.approval?.status ?? "PENDING"
    const isApproved = approvalStatus === "APPROVED"
    const plan = resolvePlanRef(business)
    const tier = plan?.tier
    const subscriptionStatus = business?.subscriptionStatus ?? "ACTIVE"

    const isPremiumTier = tier === "PREMIUM"
    const isPremiumActive = isApproved && isPremiumTier && subscriptionStatus === "ACTIVE"
    const needsPayment =
      isApproved && isPremiumTier && subscriptionStatus === "PENDING_PAYMENT"
    const needsUpgrade = isApproved && tier === "STARTER"

    const canAccess = (feature: BusinessPlanFeature) => {
      if (!isApproved) return false
      if (tier === "STARTER") return false
      if (subscriptionStatus !== "ACTIVE") return false
      const apiFeature = FEATURE_MAP[feature]
      return (plan?.features ?? []).includes(apiFeature)
    }

    return {
      businessId,
      isLoading: !!businessId && isLoading,
      isApproved,
      tier,
      subscriptionStatus,
      canUseCore: isApproved,
      canUsePremium: isPremiumActive,
      needsPayment,
      needsUpgrade,
      canAccess,
    }
  }, [businessData, businessId, isLoading])
}
